import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ActionButton, ActionButtonCluster } from 'styles/common.styles';
import { theme, cssVar } from 'styles/theme';

function storageKeyOf(prefix, prefsUserId) {
  return [prefix || 'screenChatCollapsed', prefsUserId || 'anon'].join('|');
}

function readCollapsed(prefix, prefsUserId) {
  try {
    const raw = localStorage.getItem(storageKeyOf(prefix, prefsUserId));
    if (raw === 'Y') return true;
    if (raw === 'N') return false;
  } catch {
    /* ignore */
  }
  return false;
}

function writeCollapsed(prefix, prefsUserId, collapsed) {
  try {
    localStorage.setItem(storageKeyOf(prefix, prefsUserId), collapsed ? 'Y' : 'N');
  } catch {
    /* ignore */
  }
}

const Shell = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  width: ${(p) => (p.$collapsed ? '28px' : '300px')};
  min-height: 0;
  height: 100%;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.md};
  background: ${cssVar('color-bg-white', theme.colors.white)};
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.xxxs};
  padding: ${theme.spacing.xxs} ${theme.spacing.xs};
  border-bottom: 1px solid ${cssVar('color-border', theme.colors.border)};
  background: ${cssVar('color-bg-light', theme.colors.background.light)};
  flex-shrink: 0;
`;

const Title = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${cssVar('color-text', theme.colors.text.primary)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${cssVar('color-text-tertiary', theme.colors.text.tertiary)};
`;

const Messages = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: ${theme.spacing.xs};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xxs};
`;

const Bubble = styled.div`
  align-self: ${(p) => (p.$mine ? 'flex-end' : 'flex-start')};
  max-width: 100%;
  padding: ${theme.spacing.xxs} ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.md};
  background: ${(p) =>
    p.$mine
      ? cssVar('color-primary-light', theme.colors.primaryLight)
      : cssVar('color-bg-medium', theme.colors.background.medium)};
  color: ${cssVar('color-text', theme.colors.text.primary)};
  font-size: ${theme.typography.fontSize.sm};
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
`;

const Composer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xxxs};
  padding: ${theme.spacing.xxs} ${theme.spacing.xs};
  border-top: 1px solid ${cssVar('color-border', theme.colors.border)};
  flex-shrink: 0;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 56px;
  max-height: 96px;
  resize: none;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.xxs} ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.sm};
  font-family: inherit;
  color: ${cssVar('color-text', theme.colors.text.primary)};
  background: ${cssVar('color-bg-input', theme.colors.background.input)};
  &:focus {
    outline: 2px solid ${cssVar('color-focus-ring', theme.colors.focusRing)};
    outline-offset: 1px;
  }
`;

const CollapseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  font-size: ${theme.typography.fontSize.sm};
  padding: 0 ${theme.spacing.xxxs};
  min-height: var(--erp-btn-min-height, 26px);
`;

/**
 * 화면 우측 업무 챗 셸 — 부서 도우미·지출결의 type1 챗봇 공통
 * send({ message, confirm, previewId }) → { type, message, previewId?, ... }
 */
export default function ScreenChatPanel({
  title = '챗봇',
  hint = '',
  welcome = '',
  placeholder = '',
  storagePrefix = 'screenChatCollapsed',
  prefsUserId = '',
  send,
  onDone,
}) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(storagePrefix, prefsUserId));
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewId, setPreviewId] = useState('');
  const [messages, setMessages] = useState(() =>
    welcome ? [{ role: 'assistant', content: welcome }] : []
  );
  const bottomRef = useRef(null);

  useEffect(() => {
    writeCollapsed(storagePrefix, prefsUserId, collapsed);
  }, [storagePrefix, prefsUserId, collapsed]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages, collapsed]);

  const push = useCallback((role, content, extra) => {
    setMessages((prev) => [...prev, { role, content, ...extra }]);
  }, []);

  const runSend = useCallback(
    async (payload) => {
      if (typeof send !== 'function') {
        push('assistant', '챗봇을 사용할 수 없습니다.');
        return;
      }
      setBusy(true);
      try {
        const data = await send(payload);
        const msg = data?.message || '응답이 없습니다.';
        const type = data?.type || '';
        if (type === 'preview' && data?.previewId) {
          setPreviewId(data.previewId);
          push('assistant', msg, { preview: true, previewId: data.previewId });
        } else {
          if (type === 'done' || type === 'error') {
            setPreviewId('');
          }
          push('assistant', msg);
          if (type === 'done' && typeof onDone === 'function') {
            await onDone(data);
          }
        }
      } catch (e) {
        push('assistant', e?.message || '요청에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [send, onDone, push]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      const raw = text.trim();
      if (!raw || busy) return;
      setText('');
      push('user', raw);

      const confirmWord = /^(확인|적용|응|네|좋아요)$/;
      const cancelWord = /^(취소|아니|아니요)$/;
      if (previewId && confirmWord.test(raw)) {
        await runSend({ message: raw, confirm: true, previewId });
        return;
      }
      if (previewId && cancelWord.test(raw)) {
        setPreviewId('');
        push('assistant', '적용을 취소했습니다.');
        return;
      }
      await runSend({ message: raw, confirm: false });
    },
    [text, busy, previewId, push, runSend]
  );

  const applyPreview = useCallback(async () => {
    if (!previewId || busy) return;
    push('user', '적용');
    await runSend({ message: '적용', confirm: true, previewId });
  }, [previewId, busy, push, runSend]);

  const cancelPreview = useCallback(() => {
    setPreviewId('');
    push('assistant', '적용을 취소했습니다.');
  }, [push]);

  const lastPreview = useMemo(
    () => [...messages].reverse().find((m) => m.preview && m.previewId === previewId),
    [messages, previewId]
  );

  if (collapsed) {
    return (
      <Shell $collapsed>
        <CollapseBtn
          type="button"
          title={`${title} 열기`}
          onClick={() => setCollapsed(false)}
          style={{ writingMode: 'vertical-rl', height: '100%', width: '100%' }}
        >
          {title}
        </CollapseBtn>
      </Shell>
    );
  }

  return (
    <Shell>
      <Head>
        <div>
          <Title>{title}</Title>
          {hint ? <Hint>{hint}</Hint> : null}
        </div>
        <CollapseBtn type="button" title="접기" onClick={() => setCollapsed(true)}>
          ›
        </CollapseBtn>
      </Head>
      <Messages>
        {messages.map((m, i) => (
          <Bubble key={i} $mine={m.role === 'user'}>
            {m.content}
          </Bubble>
        ))}
        <div ref={bottomRef} />
      </Messages>
      {lastPreview ? (
        <Composer as="div">
          <ActionButtonCluster>
            <ActionButton type="button" $variant="success" disabled={busy} onClick={applyPreview}>
              적용
            </ActionButton>
            <ActionButton type="button" disabled={busy} onClick={cancelPreview}>
              취소
            </ActionButton>
          </ActionButtonCluster>
        </Composer>
      ) : null}
      <Composer onSubmit={handleSubmit}>
        <TextArea
          value={text}
          disabled={busy}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <ActionButtonCluster>
          <ActionButton type="submit" $variant="primary" disabled={busy || !text.trim()}>
            보내기
          </ActionButton>
        </ActionButtonCluster>
      </Composer>
    </Shell>
  );
}
